'use client';

import * as React from 'react';
import { useAIInteraction } from '@/components/organisms/ai/_shared/interaction-context';
import { validateTableBlockSchema } from '@/components/organisms/ai/_shared/schema-validation';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

/**
 * Schema for a table column
 */
export interface TableColumnSchema {
  /** Unique key for the column, maps to data field */
  key: string;
  /** Display label for the column header */
  label: string;
  /** Whether the column is sortable (not yet implemented) */
  sortable?: boolean;
  /** Whether cells in this column are editable (not yet implemented) */
  editable?: boolean;
  /** Column width - can be number (pixels) or string (e.g., '100px', '20%') */
  width?: number | string;
}

/**
 * Schema for configuring an AITable
 */
export interface TableBlockSchema {
  /** Unique identifier for the component instance */
  id: string;
  /** Optional title displayed above the table */
  title?: string;
  /** Column definitions */
  columns: TableColumnSchema[];
  /** Array of row data */
  data: Record<string, unknown>[];
  /** Whether rows can be selected with checkboxes */
  selectable?: boolean;
  /** Pagination configuration (not yet implemented) */
  pagination?: boolean | { pageSize: number };
}

/**
 * Props for AITable
 */
export interface AITableProps {
  /** Schema defining the table configuration */
  schema: TableBlockSchema;
  /** Additional class names */
  className?: string;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get the row ID from a data row
 * Uses 'id' field if available, otherwise falls back to first column key
 */
function getRowId(
  row: Record<string, unknown>,
  columns: TableColumnSchema[],
  index: number,
): string {
  if (row.id !== undefined) {
    const id = String(row.id);
    if (id) {
      return id;
    }
  }
  if (columns.length > 0) {
    const firstKey = columns[0].key;
    const id = String(row[firstKey] ?? '');
    if (id) {
      return id;
    }
  }
  return `row-${index + 1}`;
}

function InvalidTableSchema({
  className,
}: {
  className?: string;
}): React.ReactElement {
  return (
    <div
      data-slot="ai-table"
      role="alert"
      className={cn(
        'rounded-lg border border-destructive bg-destructive/20 p-4 text-sm text-destructive',
        className,
      )}
    >
      Invalid table schema
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

/**
 * AITable - Renders interactive data tables from JSON schema.
 *
 * Supports row selection with checkboxes and emits interaction events
 * through the AIInteractionContext when selections change.
 */
export function AITable({
  schema,
  className,
}: AITableProps): React.ReactElement {
  const { onInteraction } = useAIInteraction();
  const tableSchema = React.useMemo(
    () => (validateTableBlockSchema(schema) ? schema : null),
    [schema],
  );
  const [selectedRows, setSelectedRows] = React.useState<Set<string>>(
    new Set(),
  );

  const id = tableSchema?.id ?? '';
  const title = tableSchema?.title;
  const columns = tableSchema?.columns ?? [];
  const data = tableSchema?.data ?? [];
  const selectable = tableSchema?.selectable ?? false;

  // Get all row IDs
  const allRowIds = React.useMemo(
    () => data.map((row, index) => getRowId(row, columns, index)),
    [data, columns],
  );

  // Effective selection: stored ids that no longer exist in data are ignored.
  const effectiveSelected = React.useMemo(() => {
    const ids = new Set(allRowIds);
    return new Set(Array.from(selectedRows).filter((r) => ids.has(r)));
  }, [selectedRows, allRowIds]);

  // Emit interaction event when the user changes selection
  const emitSelectionChange = React.useCallback(
    (newSelectedRows: string[]) => {
      onInteraction?.({
        componentId: id,
        componentType: 'table',
        action: 'select',
        value: { selectedRows: newSelectedRows },
        timestamp: Date.now(),
      });
    },
    [id, onInteraction],
  );

  // Handle individual row selection
  const handleRowSelect = React.useCallback(
    (rowId: string, checked: boolean) => {
      const next = new Set(effectiveSelected);
      if (checked) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      setSelectedRows(next);
      emitSelectionChange(Array.from(next));
    },
    [effectiveSelected, emitSelectionChange],
  );

  // Handle select all
  const handleSelectAll = React.useCallback(
    (checked: boolean) => {
      const next = checked ? allRowIds : [];
      setSelectedRows(new Set(next));
      emitSelectionChange(next);
    },
    [allRowIds, emitSelectionChange],
  );

  // Focus handoff: when the focused row leaves the data, move focus to the
  // nearest surviving row's checkbox (next, else previous), else select-all,
  // else the table region. Refs record state the removed DOM can no longer give.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const focusedRowIdRef = React.useRef<string | null>(null);
  const prevRowIdsRef = React.useRef<string[]>(allRowIds);
  // Callback ref: the root node is replaced when toggling empty <-> populated.
  const attachRoot = React.useCallback((root: HTMLDivElement | null) => {
    rootRef.current = root;
    if (!root) return;
    const onIn = (e: FocusEvent) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>(
        '[data-row-id]',
      );
      if (row) focusedRowIdRef.current = row.dataset.rowId ?? null;
    };
    // Removal commits in the same task as its focusout, so a target still
    // connected one microtask later was a real blur, not a removal.
    const onOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      const blurredId =
        target.closest<HTMLElement>('[data-row-id]')?.dataset.rowId;
      queueMicrotask(() => {
        if (target.isConnected && focusedRowIdRef.current === blurredId) {
          focusedRowIdRef.current = null;
        }
      });
    };
    root.addEventListener('focusin', onIn);
    root.addEventListener('focusout', onOut);
  }, []);
  React.useLayoutEffect(() => {
    const prevIds = prevRowIdsRef.current;
    prevRowIdsRef.current = allRowIds;
    const focusedId = focusedRowIdRef.current;
    const root = rootRef.current;
    if (focusedId === null || !root || allRowIds.includes(focusedId)) return;
    focusedRowIdRef.current = null;
    const doc = root.ownerDocument;
    if (doc.activeElement && doc.activeElement !== doc.body) return;
    const at = prevIds.indexOf(focusedId);
    const survivors = new Set(allRowIds);
    const heir =
      prevIds.slice(at + 1).find((r) => survivors.has(r)) ??
      prevIds
        .slice(0, Math.max(at, 0))
        .reverse()
        .find((r) => survivors.has(r));
    const rows = Array.from(
      root.querySelectorAll<HTMLElement>('[data-row-id]'),
    );
    const target =
      rows
        .find((r) => r.dataset.rowId === heir)
        ?.querySelector<HTMLElement>('[role="checkbox"]') ??
      root.querySelector<HTMLElement>('thead [role="checkbox"]') ??
      root;
    target.focus();
  }, [allRowIds]);

  // Drop stale ids from stored state so re-added rows are not revived.
  // State only: no interaction event is emitted for data replacement.
  React.useLayoutEffect(() => {
    if (effectiveSelected.size !== selectedRows.size) {
      setSelectedRows(effectiveSelected);
    }
  }, [effectiveSelected, selectedRows]);

  // Check if all rows are selected
  const allSelected =
    allRowIds.length > 0 && effectiveSelected.size === allRowIds.length;
  const someSelected =
    effectiveSelected.size > 0 && effectiveSelected.size < allRowIds.length;

  if (!tableSchema) {
    return <InvalidTableSchema className={className} />;
  }

  // Empty state
  if (!data || data.length === 0) {
    // Same element type and position as the populated root, so the node (and
    // its focus listeners) persists and doubles as the focus fallback target.
    return (
      <div
        ref={attachRoot}
        data-slot="ai-table"
        role="status"
        aria-label={title ?? 'Table'}
        tabIndex={-1}
        className={cn(
          'flex items-center justify-center rounded-lg border border-border bg-muted p-8 text-muted-foreground outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
          className,
        )}
      >
        No data available
      </div>
    );
  }

  return (
    // A <section> would change element type vs the empty state, remount the
    // node and null rootRef before the focus-handoff effect runs.
    // Both branches must stay the same `div` type: switching element type
    // remounts the node, so the ref callback nulls rootRef before the layout
    // effect reads it and the fallback focus target is lost.
    // biome-ignore lint/a11y/useSemanticElements: same-type div keeps the focus fallback node stable
    <div
      ref={attachRoot}
      data-slot="ai-table"
      role="region"
      aria-label={title ?? 'Table'}
      tabIndex={-1}
      className={cn(
        'flex flex-col gap-2 rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
        className,
      )}
    >
      {title && (
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
      )}

      {/* Live region for selection count */}
      {selectable && (
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {effectiveSelected.size > 0
            ? `${effectiveSelected.size} of ${allRowIds.length} rows selected`
            : 'No rows selected'}
        </div>
      )}

      <Table aria-label={title ?? 'Interactive data table'}>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all rows"
                />
              </TableHead>
            )}
            {columns.map((column) => (
              <TableHead
                key={column.key}
                style={{
                  width:
                    typeof column.width === 'number'
                      ? Number.isFinite(column.width)
                        ? `${column.width}px`
                        : undefined
                      : column.width,
                }}
              >
                {column.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row, index) => {
            const rowId = getRowId(row, columns, index);
            const isSelected = effectiveSelected.has(rowId);

            return (
              <TableRow
                key={rowId}
                data-row-id={rowId}
                data-state={isSelected ? 'selected' : undefined}
                className="transition-colors duration-(--duration-base) ease-smooth motion-reduce:transition-none"
              >
                {selectable && (
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(checked) =>
                        handleRowSelect(rowId, checked === true)
                      }
                      aria-label={`Select row ${rowId}`}
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    {String(row[column.key] ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

AITable.displayName = 'AITable';

export default AITable;
